'use client'

import {
  ExternalLink,
  MessageSquare,
  ArrowUp,
  Clock,
  User,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
} from 'lucide-react'
import { sanitizeHref } from '@/lib/sanitize'

interface SeedingSignal {
  signal: string
  points: number
}

interface MentionCardProps {
  title: string
  body: string
  author: string
  subreddit: string
  score: number
  numComments: number
  createdUtc: number
  permalink: string
  seedingRisk: number
  riskLevel: 'low' | 'medium' | 'high'
  seedingSignals: SeedingSignal[]
  gptRefined: boolean
  sentiment: 'positive' | 'neutral' | 'negative'
  accountAgeDays: number | null
  totalKarma: number | null
}

const RISK_COLORS: Record<string, string> = {
  low: '#22c55e',
  medium: '#FDC304',
  high: '#F90B8A',
}

const RISK_ICONS: Record<string, React.ReactNode> = {
  low: <ShieldCheck size={14} />,
  medium: <ShieldQuestion size={14} />,
  high: <ShieldAlert size={14} />,
}

const SENTIMENT_LABELS: Record<string, { label: string; color: string }> = {
  positive: { label: 'Positive', color: '#22c55e' },
  neutral: { label: 'Neutral', color: '#FDC304' },
  negative: { label: 'Negative', color: '#F90B8A' },
}

function timeAgo(utc: number): string {
  const seconds = Math.floor(Date.now() / 1000 - utc)
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  if (seconds < 2592000) return `${Math.floor(seconds / 86400)}d ago`
  if (seconds < 31536000) return `${Math.floor(seconds / 2592000)}mo ago`
  return `${Math.floor(seconds / 31536000)}y ago`
}

export default function MentionCard({
  title,
  body,
  author,
  subreddit,
  score,
  numComments,
  createdUtc,
  permalink,
  riskLevel,
  seedingSignals,
  gptRefined,
  sentiment,
  accountAgeDays,
  totalKarma,
}: MentionCardProps) {
  const riskColor = RISK_COLORS[riskLevel]
  const sentimentInfo = SENTIMENT_LABELS[sentiment]
  const safePermalink = sanitizeHref(permalink, {
    allowedSchemes: ['https:'],
  })

  return (
    <div className='bg-background border border-border/60 rounded-xl p-4 flex flex-col gap-3'>
      {/* Header: subreddit + risk badge */}
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2 min-w-0'>
          <span className='text-xs text-muted-foreground'>r/{subreddit}</span>
          <span className='text-xs text-muted-foreground'>&middot;</span>
          <span className='text-xs text-muted-foreground flex items-center gap-1'>
            <Clock size={11} />
            {timeAgo(createdUtc)}
          </span>
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          <span
            className='text-xs font-medium rounded-full px-2 py-0.5 flex items-center gap-1'
            style={{
              backgroundColor: `${sentimentInfo.color}15`,
              color: sentimentInfo.color,
            }}
          >
            {sentimentInfo.label}
          </span>
          <span
            className='text-xs font-bold rounded-full px-2 py-0.5 flex items-center gap-1'
            style={{ backgroundColor: `${riskColor}15`, color: riskColor }}
          >
            {RISK_ICONS[riskLevel]}
            {riskLevel === 'low'
              ? 'Organic'
              : riskLevel === 'medium'
                ? 'Suspicious'
                : 'Likely Seeded'}
          </span>
        </div>
      </div>

      {/* Title */}
      {safePermalink ? (
        <a
          href={safePermalink}
          target='_blank'
          rel='noopener noreferrer'
          className='text-sm font-semibold text-foreground hover:text-primary transition-colors leading-snug flex items-start gap-1.5'
        >
          <span className='line-clamp-2'>{title}</span>
          <ExternalLink
            size={14}
            className='shrink-0 mt-0.5 text-muted-foreground'
          />
        </a>
      ) : (
        <p className='text-sm font-semibold text-foreground leading-snug'>
          <span className='line-clamp-2'>{title}</span>
        </p>
      )}

      {/* Body preview */}
      {body && (
        <p className='text-xs text-foreground/60 leading-relaxed line-clamp-3'>
          {body}
        </p>
      )}

      {/* Footer: author, score, comments */}
      <div className='flex items-center justify-between text-xs text-muted-foreground'>
        <div className='flex items-center gap-3'>
          <span className='flex items-center gap-1'>
            <User size={11} />
            u/{author}
            {accountAgeDays !== null && (
              <span className='text-foreground/70'>({accountAgeDays}d)</span>
            )}
          </span>
          {totalKarma !== null && (
            <span className='text-foreground/70'>
              {totalKarma.toLocaleString()} karma
            </span>
          )}
        </div>
        <div className='flex items-center gap-3'>
          <span className='flex items-center gap-1'>
            <ArrowUp size={11} />
            {score}
          </span>
          <span className='flex items-center gap-1'>
            <MessageSquare size={11} />
            {numComments}
          </span>
        </div>
      </div>

      {/* Seeding signals (collapsed for low risk) */}
      {seedingSignals.length > 0 && riskLevel !== 'low' && (
        <div
          className='rounded-lg p-2.5 space-y-1'
          style={{
            backgroundColor: `${riskColor}08`,
            border: `1px solid ${riskColor}20`,
          }}
        >
          <div className='flex items-center gap-1.5 mb-1'>
            <span
              className='text-[10px] font-semibold uppercase tracking-wide'
              style={{ color: riskColor }}
            >
              Seeding Signals
              {gptRefined && (
                <span className='text-muted-foreground font-normal ml-1'>
                  (AI verified)
                </span>
              )}
            </span>
          </div>
          {seedingSignals.map((s, i) => (
            <div
              key={i}
              className='flex items-center justify-between text-[11px] text-foreground/60'
            >
              <span>{s.signal}</span>
              <span style={{ color: riskColor }}>+{s.points}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
